/**
 * Action-oriented demo primitives: buttons, toolbar groups, keyboard
 * hints, and copyable link rows.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { GRAY, PRIMARY, WARN } from "../../design-system/tokens.js";
import { useCopyToClipboard } from "../../hooks/index.js";
import { focusRingStyle, MONO_FONT } from "./styles.js";

export type DemoButtonVariant = "default" | "primary" | "ghost" | "danger";
export type DemoButtonSize = "sm" | "md";

export interface DemoButtonProps {
  readonly children: React.ReactNode;
  readonly onClick?: () => void;
  readonly disabled?: boolean;
  readonly ariaLabel: string;
  readonly variant?: DemoButtonVariant;
  readonly size?: DemoButtonSize;
  readonly type?: "button" | "submit" | "reset";
  readonly title?: string;
  readonly fullWidth?: boolean;
}

const BUTTON_VARIANT: Readonly<
  Record<DemoButtonVariant, Pick<React.CSSProperties, "background" | "borderColor" | "color">>
> = {
  default: { background: "white", borderColor: GRAY[300], color: GRAY[700] },
  primary: { background: PRIMARY[600], borderColor: PRIMARY[600], color: "white" },
  ghost: { background: "transparent", borderColor: "transparent", color: GRAY[700] },
  danger: { background: WARN[100], borderColor: WARN[300], color: WARN[700] },
};

const BUTTON_SIZE: Readonly<
  Record<DemoButtonSize, Pick<React.CSSProperties, "fontSize" | "padding">>
> = {
  sm: { fontSize: 11, padding: "0.375rem 0.625rem" },
  md: { fontSize: 12, padding: "0.5rem 0.75rem" },
};

export function DemoButton({
  children,
  onClick,
  disabled = false,
  ariaLabel,
  variant = "default",
  size = "md",
  type = "button",
  title,
  fullWidth = false,
}: DemoButtonProps): React.JSX.Element {
  return (
    <button
      type={type}
      disabled={disabled}
      aria-label={ariaLabel}
      title={title}
      onClick={onClick}
      style={{
        ...BUTTON_VARIANT[variant],
        ...BUTTON_SIZE[size],
        ...focusRingStyle(),
        alignItems: "center",
        borderRadius: 8,
        borderStyle: "solid",
        borderWidth: 1,
        boxShadow: variant === "ghost" ? "none" : "0 1px 2px rgba(15, 23, 42, 0.05)",
        cursor: disabled ? "not-allowed" : "pointer",
        display: "inline-flex",
        fontWeight: 600,
        gap: 6,
        justifyContent: "center",
        lineHeight: 1.15,
        minHeight: size === "sm" ? 28 : 34,
        opacity: disabled ? 0.45 : 1,
        transition: "background-color 120ms ease, border-color 120ms ease, color 120ms ease",
        width: fullWidth ? "100%" : undefined,
      }}
    >
      {children}
    </button>
  );
}

export function DemoKbd({ children }: { readonly children: React.ReactNode }): React.JSX.Element {
  return (
    <kbd
      style={{
        alignItems: "center",
        background: "white",
        border: `1px solid ${GRAY[400]}`,
        borderBottomWidth: 2,
        borderRadius: 6,
        color: GRAY[700],
        display: "inline-flex",
        fontFamily: MONO_FONT,
        fontSize: 10.5,
        height: "1.25rem",
        justifyContent: "center",
        lineHeight: 1,
        minWidth: "1.25rem",
        padding: "0 0.35rem",
      }}
    >
      {children}
    </kbd>
  );
}

export function DemoToolbar({
  children,
  ariaLabel = "Actions",
}: {
  readonly children: React.ReactNode;
  readonly ariaLabel?: string;
}): React.JSX.Element {
  return (
    <div
      role="toolbar"
      aria-label={ariaLabel}
      style={{ alignItems: "center", display: "flex", flexWrap: "wrap", gap: 8 }}
    >
      {children}
    </div>
  );
}

export function DemoToolbarGroup({
  label,
  children,
}: {
  readonly label?: string;
  readonly children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div
      style={{
        alignItems: "center",
        background: "white",
        border: `1px solid ${GRAY[300]}`,
        borderRadius: 8,
        display: "inline-flex",
        gap: 4,
        padding: 4,
      }}
    >
      {label !== undefined && (
        <span
          style={{
            color: GRAY[500],
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: "0.04em",
            padding: "0 0.4rem 0 0.2rem",
            textTransform: "uppercase",
          }}
        >
          {label}
        </span>
      )}
      {children}
    </div>
  );
}

export interface DemoCopyableLinkRowProps {
  readonly link: string;
  readonly onCopied?: (link: string) => void;
}

export function DemoCopyableLinkRow({
  link,
  onCopied,
}: DemoCopyableLinkRowProps): React.JSX.Element {
  const { copied, copy } = useCopyToClipboard({ resetAfterMs: 1200 });
  return (
    <div
      style={{
        alignItems: "center",
        background: "white",
        border: `1px solid ${GRAY[300]}`,
        borderRadius: 8,
        display: "flex",
        gap: 12,
        justifyContent: "space-between",
        padding: "0.375rem 0.625rem",
      }}
    >
      <div
        title={link}
        style={{
          color: GRAY[700],
          flex: "1 1 auto",
          fontFamily: MONO_FONT,
          fontSize: 11,
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {link}
      </div>
      <DemoButton
        size="sm"
        variant="ghost"
        ariaLabel={`Copy link ${link}`}
        onClick={() => {
          void (async (): Promise<void> => {
            const ok = await copy(link);
            if (ok) onCopied?.(link);
          })();
        }}
      >
        {copied ? "Copied" : "Copy"}
      </DemoButton>
    </div>
  );
}
